import { useEffect, useState, useCallback } from "react";
import { api, apiError, API, getToken } from "../lib/api";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { UploadSimple, XCircle, CheckCircle, Warning, Eye, Image as ImageIcon, VideoCamera, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { compressImage, compressVideo } from "../lib/media";

const TYPE_META = {
  image: { icon: ImageIcon, accept: "image/*", label: "Image" },
  video: { icon: VideoCamera, accept: "video/*", label: "Video" },
  pdf: { icon: FilePdf, accept: "application/pdf,.pdf", label: "PDF" },
  any: { icon: UploadSimple, accept: "image/*,video/*,application/pdf,.pdf", label: "Any file" },
};
const MAX_BYTES = 5 * 1024 * 1024;

async function prepareFile(file, fileType) {
  const isImg = file.type.startsWith("image/");
  const isVid = file.type.startsWith("video/");
  if (fileType === "image" || (fileType === "any" && isImg)) return compressImage(file);
  if (fileType === "video" || (fileType === "any" && isVid)) return compressVideo(file, 5);
  return file;
}

export function JobMediaUploads({ jobId, canUpload = true }) {
  const [placeholders, setPlaceholders] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!jobId) return;
    setLoading(true);
    api.get(`/jobs/${jobId}/media-requirements`)
      .then((r) => { setPlaceholders(r.data.placeholders || []); setUploads(r.data.uploads || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const filesFor = (pid) => uploads.filter((u) => u.placeholder_id === pid);
  const viewUrl = (u) => `${API}/jobs/${jobId}/media/${u.id}?auth=${getToken()}`;

  const upload = async (ph, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusyId(ph.id);
    try {
      let out = file;
      try { out = await prepareFile(file, ph.file_type); } catch { out = file; }
      if (out.size > MAX_BYTES) {
        toast.error("File is over 5MB even after compression — please use a smaller/shorter file");
        setBusyId(""); return;
      }
      const fd = new FormData();
      fd.append("file", out);
      fd.append("placeholder_id", ph.id);
      fd.append("placeholder_name", ph.name);
      fd.append("file_type", ph.file_type);
      await api.post(`/jobs/${jobId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${ph.name} uploaded`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusyId(""); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove "${u.placeholder_name}"?`)) return;
    try {
      await api.delete(`/jobs/${jobId}/media/${u.id}`);
      toast.success("File removed");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (loading) return <p className="text-xs text-muted-foreground" data-testid="job-media-loading">Loading media requirements…</p>;
  if (!placeholders.length) return (
    <p className="text-xs text-muted-foreground" data-testid="job-media-none">
      No brand/category media requirements configured for this job.
    </p>
  );

  return (
    <div className="space-y-3" data-testid="job-media-uploads">
      {placeholders.map((ph) => {
        const Meta = TYPE_META[ph.file_type] || TYPE_META.any;
        const Icon = Meta.icon;
        const files = filesFor(ph.id);
        const done = files.length > 0;
        return (
          <div key={ph.id} className="border border-border rounded-sm p-3" data-testid={`media-ph-${ph.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Icon size={16} className="shrink-0 text-muted-foreground" />
                <span className="text-sm font-medium truncate">{ph.name}</span>
                {ph.required
                  ? <span className="text-[10px] font-bold uppercase text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-sm">Required</span>
                  : <span className="text-[10px] font-semibold uppercase text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-sm">Optional</span>}
              </div>
              {done
                ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" data-testid={`media-done-${ph.id}`} />
                : (ph.required && <Warning size={16} weight="fill" className="text-amber-500 shrink-0" />)}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{Meta.label} · max 5MB (auto-compressed)</p>
            <div className="mt-2 space-y-1.5">
              {files.map((u) => (
                <div key={u.id} className="flex items-center justify-between border border-border rounded-sm px-2 py-1.5 text-xs bg-secondary/40" data-testid={`media-file-${u.id}`}>
                  <a href={viewUrl(u)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary font-medium min-w-0">
                    <Eye size={13} className="shrink-0" /> <span className="truncate">{u.original_filename || ph.name}</span>
                  </a>
                  {canUpload && (
                    <button onClick={() => remove(u)} className="text-destructive shrink-0" title="Remove" data-testid={`media-remove-${u.id}`}><XCircle size={14} /></button>
                  )}
                </div>
              ))}
            </div>
            {canUpload && (
              <label className="mt-2 block">
                <input type="file" accept={Meta.accept} className="hidden" onChange={(e) => upload(ph, e)} disabled={busyId === ph.id} data-testid={`media-input-${ph.id}`} />
                <Button asChild variant="outline" size="sm" className="rounded-sm gap-1.5 w-full" disabled={busyId === ph.id}>
                  <span className="cursor-pointer" data-testid={`media-upload-${ph.id}`}>
                    <UploadSimple size={14} /> {busyId === ph.id ? "Uploading…" : (done ? "Upload another" : "Upload")}
                  </span>
                </Button>
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}
